'use client';
import React, { useState } from 'react';
import AnnouncementBar from '@/components/AnnouncementBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: FaqItem[] = [
  {
    question: 'What is a Clinical Psychologist?',
    answer: 'A Clinical Psychologist is a highly trained mental health professional who has completed a doctoral-level qualification (DClinPsy) in clinical psychology. They are trained to assess, diagnose and treat a wide range of psychological difficulties using evidence-based approaches. Clinical Psychologists are regulated by the Health and Care Professions Council (HCPC) and are bound by strict professional codes of conduct.',
  },
  {
    question: 'What is the difference between a Clinical Psychologist and a psychiatrist?',
    answer: 'A psychiatrist is a medical doctor who has specialised in mental health and can prescribe medication. A Clinical Psychologist has a doctoral-level qualification in psychology and specialises in psychological assessment and therapy. Both work with mental health difficulties, but their training and approaches differ. Clinical Psychologists focus on understanding and changing patterns of thought, feeling and behaviour through talking therapies.',
  },
  {
    question: 'What happens at the first appointment?',
    answer: 'Your initial appointment lasts one hour and is an opportunity for Dr Morgan to gather information from you about how you see your current difficulties and how you would like therapy to move forward. It is also an opportunity for you to ask questions and decide whether you feel comfortable working with Dr Morgan. You are under no obligation to proceed with further sessions after the initial assessment.',
  },
  {
    question: 'How many sessions will I need?',
    answer: 'The number of sessions varies depending on the nature and complexity of your difficulties, and your personal goals for therapy. Dr Morgan is experienced in working within short time frameworks, providing maximum interventions in minimum time. The number of sessions will be discussed and agreed with you following the initial assessment.',
  },
  {
    question: 'Is everything I say kept confidential?',
    answer: 'Yes. All services are bound by professional codes of conduct, so a confidential service is assured. There are very limited exceptions to confidentiality — for example, if Dr Morgan believed you or someone else was at serious risk of harm. These exceptions would always be discussed with you wherever possible. If you require particular discretion around consultations, please contact Dr Morgan to discuss this further.',
  },
  {
    question: 'Do I need a GP referral?',
    answer: 'No. You can self-refer directly to Dr Morgan without a GP referral. However, Dr Morgan may recommend that you inform your GP that you are receiving private psychological therapy, and she can liaise with your GP if you wish.',
  },
  {
    question: 'Do you offer online sessions?',
    answer: 'Yes. Dr Morgan is based in Wales and offers online sessions to clients throughout the world. Online therapy can be just as effective as face-to-face therapy for many difficulties. Please contact Dr Morgan directly to discuss whether online therapy is appropriate for your needs.',
  },
  {
    question: 'What therapeutic approaches does Dr Morgan use?',
    answer: 'Dr Morgan employs an integrative approach, drawing on a range of evidence-based therapies including Cognitive Behavioural Therapy (CBT), Cognitive Analytic Therapy (CAT), Mindfulness-Based Cognitive Therapy (MCT), Systemic Therapy (ST) and Psychodynamic Psychotherapy (PPT). CBT is the main mode of therapy offered. Any intervention is based upon individual need and the formulation of your current difficulties.',
  },
  {
    question: 'What are your fees?',
    answer: '[NO INFORMATION PROVIDED — insert real fee information here. Do not publish until confirmed by Dr Morgan.]',
    isPlaceholder: true,
  } as FaqItem & { isPlaceholder?: boolean },
  {
    question: 'How do I book an appointment?',
    answer: 'You can arrange an appointment by contacting Dr Morgan directly via email or telephone, or by using the online booking system on the Contact page. Dr Morgan will respond to all enquiries promptly and in confidence.',
  },
  {
    question: 'What if I need to cancel or reschedule?',
    answer: '[NO INFORMATION PROVIDED — insert real cancellation policy here (e.g. notice period required, whether fees apply for late cancellations). Do not publish until confirmed by Dr Morgan.]',
    isPlaceholder: true,
  } as FaqItem & { isPlaceholder?: boolean },
  {
    question: 'Is Dr Morgan registered with health insurance providers?',
    answer: '[NO INFORMATION PROVIDED — insert real information about health insurance registration (e.g. AXA, Bupa, Vitality, etc.) here. Do not publish until confirmed by Dr Morgan.]',
    isPlaceholder: true,
  } as FaqItem & { isPlaceholder?: boolean },
];

function FaqAccordionItem({ item, isOpen, onToggle }: { item: FaqItem & { isPlaceholder?: boolean }; isOpen: boolean; onToggle: () => void }) {
  return (
    <div style={{ borderBottom: '1px solid rgba(26,35,50,0.1)' }}>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start justify-between gap-6"
        style={{
          padding: 'clamp(20px, 2.5vw, 28px) 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
        aria-expanded={isOpen}
      >
        <span
          style={{
            fontFamily: 'orpheus-pro, Georgia, serif',
            fontSize: 'clamp(17px, 1.6vw, 22px)',
            fontWeight: '400',
            color: '#1A2332',
            lineHeight: '1.35',
            flex: 1,
          }}
        >
          {item.question}
        </span>
        <span
          style={{
            flexShrink: 0,
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(26,35,50,0.2)',
            borderRadius: '50%',
            transition: 'transform 0.3s ease, background-color 0.2s ease',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
            backgroundColor: isOpen ? '#1A2332' : 'transparent',
            marginTop: '2px',
          }}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
          >
            <line x1="5" y1="0" x2="5" y2="10" stroke={isOpen ? '#F5F0EA' : '#1A2332'} strokeWidth="1.2" />
            <line x1="0" y1="5" x2="10" y2="5" stroke={isOpen ? '#F5F0EA' : '#1A2332'} strokeWidth="1.2" />
          </svg>
        </span>
      </button>

      <div
        style={{
          overflow: 'hidden',
          maxHeight: isOpen ? '600px' : '0',
          transition: 'max-height 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div style={{ paddingBottom: 'clamp(20px, 2.5vw, 28px)' }}>
          {item.isPlaceholder ? (
            <div className="placeholder-flag">
              <strong>⚠ Placeholder — Do Not Publish</strong>
              {item.answer}
            </div>
          ) : (
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(15px, 1.2vw, 18px)',
                color: '#4A4A4A',
                lineHeight: '1.8',
                maxWidth: '720px',
              }}
            >
              {item.answer}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (idx: number) => {
    setOpenIndex(prev => (prev === idx ? null : idx));
  };

  return (
    <div style={{ backgroundColor: '#EFE3E1', minHeight: '100vh' }}>
      <AnnouncementBar />
      <Header />

      <main>
        {/* ── Page Hero ── */}
        <section
          className="w-full"
          style={{
            position: 'relative',
            backgroundImage: 'url(/assets/images/6213a810-69da-445d-a2a4-7e2ee270c1b0-1782134989081.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            paddingTop: 'clamp(72px, 9vw, 128px)',
            paddingBottom: 'clamp(72px, 9vw, 128px)',
          }}
        >
          {/* Dark overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(26,35,50,0.65)',
              zIndex: 0,
            }}
          />
          <div style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '11px',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: '#8A9E8C',
                marginBottom: '20px',
              }}
            >
              Frequently Asked Questions
            </p>
            <h1
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(36px, 5vw, 72px)',
                fontWeight: '400',
                color: '#F5F0EA',
                lineHeight: '1.1',
                maxWidth: '600px',
                marginBottom: '24px',
              }}
            >
              Questions &amp;<br />
              <em style={{ fontStyle: 'normal' }}>Answers</em>
            </h1>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(15px, 1.3vw, 19px)',
                color: 'rgba(239,227,225,0.6)',
                lineHeight: '1.7',
                maxWidth: '520px',
              }}
            >
              Common questions about private psychological therapy, Dr Morgan&apos;s practice, and what to expect.
            </p>
          </div>
        </section>

        {/* ── FAQ Accordion ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#EFE3E1', paddingTop: 'clamp(64px, 8vw, 112px)', paddingBottom: 'clamp(64px, 8vw, 112px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)' }}>
            <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-24">

              {/* Left: sticky label */}
              <div className="hidden lg:block" style={{ flex: '0 0 auto', width: '240px', position: 'sticky', top: '120px' }}>
                <p
                  style={{
                    fontFamily: 'adobe-garamond-pro, Georgia, serif',
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A9E8C',
                    marginBottom: '16px',
                  }}
                >
                  Topics
                </p>
                {['About Clinical Psychology', 'The Process', 'Confidentiality', 'Practical Information'].map(topic => (
                  <p
                    key={topic}
                    style={{
                      fontFamily: 'adobe-garamond-pro, Georgia, serif',
                      fontSize: '14px',
                      color: 'rgba(26,35,50,0.45)',
                      lineHeight: '1.8',
                    }}
                  >
                    {topic}
                  </p>
                ))}
              </div>

              {/* Right: accordion */}
              <div style={{ flex: 1, borderTop: '1px solid rgba(26,35,50,0.1)' }}>
                {faqs.map((item, idx) => (
                  <FaqAccordionItem
                    key={idx}
                    item={item as FaqItem & { isPlaceholder?: boolean }}
                    isOpen={openIndex === idx}
                    onToggle={() => handleToggle(idx)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Still have questions CTA ── */}
        <section
          className="w-full"
          style={{ backgroundColor: '#F5F0EA', paddingTop: 'clamp(64px, 8vw, 96px)', paddingBottom: 'clamp(64px, 8vw, 96px)' }}
        >
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 clamp(24px, 4vw, 64px)', textAlign: 'center' }}>
            <h2
              style={{
                fontFamily: 'orpheus-pro, Georgia, serif',
                fontSize: 'clamp(24px, 3vw, 44px)',
                fontWeight: '400',
                color: '#1A2332',
                lineHeight: '1.2',
                marginBottom: '16px',
              }}
            >
              Still have questions?
            </h2>
            <p
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: 'clamp(15px, 1.2vw, 18px)',
                color: '#4A4A4A',
                lineHeight: '1.75',
                maxWidth: '480px',
                margin: '0 auto 32px',
              }}
            >
              Dr Morgan is happy to answer any questions you may have before arranging an initial consultation.
            </p>
            <a
              href="/contact"
              style={{
                fontFamily: 'adobe-garamond-pro, Georgia, serif',
                fontSize: '13px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#FAF7F2',
                backgroundColor: '#1A2332',
                padding: '15px 40px',
                textDecoration: 'none',
                display: 'inline-block',
                transition: 'background-color 0.25s ease',
              }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#2C2C2C')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.backgroundColor = '#1A2332')}
            >
              Get in Touch
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
